import {
    IndustryTag,
    SPECIAL_INDUSTRY_TAG_ALL
} from '@assets/component/IndustryTagSelect/IndustryTagSelect';
import {
    FlowTaskStateType,
    getFirebase
} from '@assets/firebase/FirebaseAdapater';
import { DataAccessUtils } from '@assets/model/DataAccessUtils';
import _ from 'lodash';
import { action, computed, observable } from 'mobx';
import moment from 'moment';
import { useEffect } from 'react';
import { ChatMessageData, RagAPI } from './RagAPI';

function isString(s) {
    return typeof s === 'string' || s instanceof String;
}

interface TranscriptEntry {
    highlight: string;
    source_text: string;
}

interface ActiveTaskJob {
    reportId: string;
    taskId: string;
    status: 'queued' | 'in_progress' | 'completed' | 'failed';
    type: 'prompt' | 'clear';
}

interface ActiveJob {
    jobId: string;
    fileName: string;
    status: string;
    task: string;
    progress: number;
}

interface UserInfo {
    isAdmin: boolean;
}

interface UserInfo {
    isAdmin: boolean;
}

type ServiceFileSummary = {
    brief_overview: string;
    doc_overview?: string;
    main_points: string[];
    topics: string[];
    risks_mentioned: string[];
    merits_mentioned: string[];
    created_at?: string;
    author?: string;
};

export type CitationEntry = {
    id: string;
    text: string;
};

export type KnowledgeFileEntry = {
    tags?: IndustryTag[];
    ragStatus?: string;
    ragDocId?: string;
    url: string;
    fileName: string;
    saveKey: string;
    overview?: string;
    industry?: string;
};
export type KnowledgeFileType = {
    [id: string]: KnowledgeFileEntry;
};
type ServiceFileStatus = 'completed' | 'pending' | 'processing' | 'error';
export type ServiceFileEntry = {
    filename: string;
    saveKey: string;
    id: string;
    summary?: ServiceFileSummary;
    url: string;
    status?: ServiceFileStatus;
    ragStatus?: ServiceFileStatus;
    // UUID v5 derived from file content by the RAG pipeline. Same content
    // produces the same ragDocId regardless of filename, which is what the
    // comparator detector uses to distinguish "new evidence" from "same file
    // re-uploaded under a different name".
    ragDocId?: string;
};
type ServiceFiles = { files: { [fileId: string]: ServiceFileEntry } };
type ServiceFileLookup = { [serviceId: string]: ServiceFiles };

type FirmInfo = {
    logoURL?: string;
};

export type ReportStep = {
    name: string;
    type: string;
    save_to?: string;
    flow_id?: string;
};

type ReportDefinition = {
    steps: ReportStep[];
};

export type Entry = {
    title: string | null; // since your Python model allowed default=None
    description: string | null;
};

// Text fields with inline citations support (backward compatible with plain strings)
export type CitationSource = {
    id: number;
    name: string;
    url: string | null;
};

export type TextWithSources = {
    text: string;
    sources: CitationSource[];
};

// Union type: accepts old string format OR new structured format with sources
export type TextFieldWithSources = string | TextWithSources | null;

export interface FlowiseFields {
    // Text fields that support inline citations with sources (backward compatible with plain strings)
    opportunity: TextFieldWithSources;
    business_overview: TextFieldWithSources;
    market_description: TextFieldWithSources;
    market_size: TextFieldWithSources;

    // list of title/desc fields
    core_product_services: Entry[] | null;
    key_customers: Entry[] | null;
    potential_customers: Entry[] | null;
    competitor_segments: Entry[] | null;
    what_we_like: Entry[] | null;
    key_risks: Entry[] | null;
    diligence: Entry[] | null;
}

interface ModalDisplay {
    title: string;
    el: JSX.Element;
}

interface SavedComparable {
    dealId: string;
    position: number;
}

interface UserComparables {
    userId: string;
    dealId: string;
    comparables: SavedComparable[];
    lastUpdated: number;
}

interface TaskStateVars {
    IS_COMPLETE: string;
    ACTIVE_STEP: string;
}

export interface StagingTurn {
    timestamp: number;
    userMessage: string;
    changeLog: string;
    source: 'comparator' | 'chat_refine';
    // Comparator (initial) turn only: what triggered it.
    reason?: string;
    sources?: string[];
}

export interface StagingMetadata {
    created_at: number;
    reason: string;
    source: 'comparator' | 'chat_refine';
    new_files: string[];
    files_snapshot: string[];
    // Append-only timeline of refinement steps. Comparator runs reset it
    // (single entry); chat_refine runs append. Persisted server-side so the
    // user sees full history across browser refreshes.
    turns?: StagingTurn[];
}

class TaskStateQuery {
    constructor(
        private taskState: FlowTaskStateEntry,
        private taskVarsExistTable: TaskVarsExistTable,
        private taskDef: TaskDefinition
    ) {}

    get isComplete(): boolean {
        if (this.taskState == null || this.taskState.state_vars == null)
            return false;

        return this.taskState.state_vars['IS_COMPLETE'] == '1';
    }

    get activeStep(): number {
        if (this.taskState == null || this.taskState.state_vars == null)
            return 0;

        if (this.isComplete) {
            return this.taskDef.inner_steps_names.length;
        }

        return parseInt(this.taskState.state_vars['ACTIVE_STEP'] ?? '0');
    }

    get isLocked(): boolean {
        if (!this.taskDef) return true;
        if (!this.taskDef.prerequisite_variable) return false;

        for (const varObj of this.taskDef.prerequisite_variable) {
            if (!this.taskVarsExistTable) return true;
            if (this.taskVarsExistTable[varObj.name] !== true) {
                return true;
            }
        }

        return false;
    }
    get status(): FlowTaskStateType {
        if (this.taskState == null) return 'not_set';

        return this.taskState.status;
    }

}
interface FlowTaskStateEntry {
    status: FlowTaskStateType;
    state_vars: TaskStateVars;
    updatedAt?: number;
    last_message_for_ui?: {
        content?: string;
        approve?: boolean;
        attachedFiles?: string[];
        attachedFilesFingerprints?: string[];
    };
    // Written by Save Canonical in the flow on first_run completion. Lists the
    // file fingerprints (ragDocId or filename) the flow consumed.
    last_run_files_snapshot?: string[];
}

type TaskVarsExistTable = { [varName: string]: boolean };

class ReportTaskState {
    constructor(
        private flowTaskState: { [taskId: string]: FlowTaskStateEntry },
        private flowTaskVarsExistTable: TaskVarsExistTable
    ) {
        if (!this.flowTaskState) {
            this.flowTaskState = {};
        }
        if (!this.flowTaskVarsExistTable) {
            this.flowTaskVarsExistTable = {};
        }
    }

    getTaskQuery(taskId: string): TaskStateQuery {
        const taskDef = useTasksStore().get_task_definition(taskId);
        return new TaskStateQuery(
            this.flowTaskState[taskId] ?? null,
            this.flowTaskVarsExistTable,
            taskDef
        );
    }
}
class Store {
    constructor() {}

    logout() {
        this.userId = null;
        this.activeJob = null;
        this.tasksLog = null;
        this.taskLogsLastIdShown = null;
    }
    @observable
    accessor selectedReportId: string = '';

    @observable
    accessor selectedTaskId: string = '';

    @observable
    accessor reportDataDB: any = null;

    @observable accessor flowiseVarsDB: {
        [reportId: string]: { [varName: string]: string };
    } = {};

    @observable accessor flowTaskVarsExistTable: {
        [reportId: string]: TaskVarsExistTable;
    } = {};

    @observable accessor flowTaskState: {
        [reportId: string]: { [taskId: string]: FlowTaskStateEntry };
    } = {};

    @observable accessor varsStagingDB: {
        [reportId: string]: {
            [keyOrVarName: string]: string | StagingMetadata;
        };
    } = {};

    getStagingFor(taskId: string, saveTo: string):
        { candidate: string; metadata: StagingMetadata | null } | null {
        const stagingByReport = this.varsStagingDB[this.selectedReportId];
        if (!stagingByReport) return null;
        const candidate = stagingByReport[saveTo];
        if (typeof candidate !== 'string' || candidate.trim() === '') return null;
        const metaKey = `${taskId}_metadata`;
        const meta = stagingByReport[metaKey];
        const metadata =
            meta && typeof meta === 'object' ? (meta as StagingMetadata) : null;
        return { candidate, metadata };
    }

    @computed get activeReportTasksState(): ReportTaskState {
        return new ReportTaskState(
            this.flowTaskState[this.selectedReportId] ?? null,
            this.flowTaskVarsExistTable[this.selectedReportId] ?? null
        );
    }

    @observable accessor knowledgeFilesDB: KnowledgeFileType;

    get isDev() {
        return process.env.NODE_ENV === 'development';
    }

    @computed get isAdmin() {
        if (this.isDev) {
            return true;
        }
        if (!this.users) return false;

        return this.users[this.userId]?.isAdmin ?? false;
    }

    @observable accessor showAdminConsoleFromKeyboardShortcut: boolean = false;
    @computed get showAdminConsole() {
        if (!this.isAdmin) {
            return false;
        }

        return this.showAdminConsoleFromKeyboardShortcut;
    }

    @observable
    accessor reportDataOverridesDB: any = {};

    @observable accessor reportsDataAccessCache: {
        [reportId: string]: DataAccessUtils;
    } = {};

    @observable
    accessor users: { [userId: string]: UserInfo } = {};

    @observable accessor userId: string;

    @observable
    accessor jobs: any = {};

    @observable
    accessor tasksLog: ActiveTaskJob[] | null = null;

    @observable
    accessor taskLogsLastIdShown: string | null = null;

    @observable
    accessor systemStatus: any = {};

    @observable accessor activeJob: ActiveJob = null;

    @observable accessor areSettingsLoaded: boolean = false;
    @observable accessor areOveridesLoaded: boolean = false;
    @observable accessor areScoreRulesLoaded: boolean = false;
    @observable accessor areKnowledgeFilesLoaded: boolean = false;
    @observable accessor areTaskDefinitionsLoaded: boolean = false;

    @observable accessor allProjectFiles: ServiceFileLookup = {};

    @observable accessor firmInfo: FirmInfo = {};
    @observable accessor reportDefinitions: { [id: string]: ReportDefinition } =
        {};

    @observable accessor isChatLoaded: boolean = false;
    @observable accessor chatData: {
        [reportId: string]: { [msgId: string]: ChatMessageData };
    } = {};

    @observable accessor ragDB: any = {};
    @observable accessor isRagDbLoaded: boolean = false;

    @observable accessor lastReportLoaded: string = '';

    @observable accessor allUserComparables: {
        [dealId: string]: {
            comparables: (string | null)[];
            lastUpdated: number;
        };
    } = {};

    @computed get userComparables(): (string | null)[] {
        if (!this.selectedReportId) return Array(2).fill(null);

        const dealData = this.allUserComparables[this.selectedReportId];
        if (!dealData?.comparables) return Array(2).fill(null);

        const comparables = [...dealData.comparables];
        while (comparables.length < 2) {
            comparables.push(null);
        }
        return comparables.slice(0, 2).map(val => val ?? null);
    }

    @computed get hasMaxComparables() {
        return (
            this.userComparables.filter(dealId => dealId !== null).length >= 2
        );
    }

    @action initializeUserComparables(
        dealId: string,
        comparables: (string | null)[]
    ) {
        if (!dealId) {
            console.warn('Cannot initialize comparables: missing dealId');
            return;
        }

        // Initialize with default comparables locally without saving to Firebase
        this.allUserComparables[dealId] = {
            comparables: comparables.map(val => val ?? null),
            lastUpdated: Date.now()
        };
    }

    async saveUserComparables(dealId: string, comparables: (string | null)[]) {
        if (!dealId || !this.userId) {
            console.warn('Cannot save comparables: missing dealId or userId');
            return;
        }

        // Ensure we always have exactly 2 slots with null for empty positions
        const normalizedComparables = comparables
            .slice(0, 2)
            .map(val => val ?? null);
        while (normalizedComparables.length < 2) {
            normalizedComparables.push(null);
        }

        try {
            const firebase = getFirebase();
            await firebase.saveUserComparables(
                this.userId,
                dealId,
                normalizedComparables
            );
        } catch (error) {
            console.error('Error saving user comparables:', error);
        }
    }

    @computed get activeReportOriginal() {
        const ticker = this.selectedReportId;
        if (!this.reportDataDB) {
            return null;
        }

        if (this.reportDataDB.hasOwnProperty(ticker)) {
            if (this.reportDataDB[ticker].status == 'processing') {
                return null;
            }

            return _.cloneDeep(this.reportDataDB[ticker]);
        }

        return null;
    }

    @computed get isLoaded() {
        return (
            this.reportDataDB != null &&
            this.areOveridesLoaded &&
            this.areScoreRulesLoaded &&
            this.areSettingsLoaded &&
            this.areKnowledgeFilesLoaded &&
            this.areTaskDefinitionsLoaded
        );
    }

    @computed get projectKnowledgeFiles(): KnowledgeFileType {
        const industry = this.query.value('industry');
        const sub_industry = this.query.value('industrySubType');
        const ret: KnowledgeFileType = {};
        for (var key in this.knowledgeFilesDB) {
            const file = this.knowledgeFilesDB[key];
            if (file.tags) {
                const tags = file.tags.map(tag => tag.value);
                if (tags.indexOf(SPECIAL_INDUSTRY_TAG_ALL) !== -1) {
                    ret[key] = file;
                    continue;
                }

                for (var i = 0; i < tags.length; i++) {
                    if (!tags[i].includes('::')) {
                        const industryType = tags[i];
                        if (industryType === industry) {
                            ret[key] = file;
                        }
                    } else {
                        const industrySubType = tags[i].split('::')[1];
                        if (industrySubType === sub_industry) {
                            ret[key] = file;
                        }
                    }
                }
            }
        }

        return ret;
    }
    @computed get projectFiles(): ServiceFiles {
        return this.allProjectFiles[this.selectedReportId] ?? { files: {} };
    }

    @computed get reportChatHistory() {
        return (
            this.chatData[this.selectedReportId] ?? {
                intro: {
                    isMe: false,
                    message: 'Hello! How can I help you today?',
                    date: new Date(),
                    processed: true
                }
            }
        );
    }

    @computed get reportChatCitationsHistory(): {
        [key: string]: string;
    } {
        const citationsTable: { [key: string]: string } = {};
        const messages = this.chatData[this.selectedReportId] ?? [];
        for (const msgId in messages) {
            const msg: ChatMessageData = messages[msgId];
            const raw = msg.raw;
            const citations: any[] = msg?.raw?.metadata?.citations ?? [];

            for (const citationEntry of citations) {
                const id = citationEntry.id;
                var text = citationEntry.payload?.text;
                if (!text) {
                    text = citationEntry.text;
                }

                // if (!text) {
                //     text = citationEntry?.payload?.summary;
                // }

                if (!text) {
                    var chunks = citationEntry?.payload?.chunks ?? [];
                    if (chunks.length > 0) {
                        text = chunks[0].text;
                    }
                }

                if (text) {
                    citationsTable[id] = text;
                }
            }
        }

        return citationsTable;
    }

    @computed get reportOverride() {
        const ticker = this.selectedReportId;
        var over_rides = {};
        if (
            this.reportDataOverridesDB &&
            this.reportDataOverridesDB.hasOwnProperty(ticker)
        ) {
            over_rides = this.reportDataOverridesDB[ticker];
        }
        return over_rides;
    }

    // @computed get selectedReportRun(): any {
    //     if (!this.reportRuns || !this.selectedReportId) {
    //         return null;
    //     }

    //     return this.reportRuns[this.selectedReportId] ?? null;
    // }

    @computed
    get query(): DataAccessUtils {
        if (!this.activeReportOriginal) {
            return null;
        }

        if (
            !this.areOveridesLoaded ||
            !this.isRagDbLoaded ||
            !this.isChatLoaded
        ) {
            return null;
        }

        return new DataAccessUtils(
            this.activeReportOriginal,
            this.reportOverride,
            this.selectedReportId
        );
    }

    @computed get report_flowise(): FlowiseFields {
        if (!this.query) return null;

        const ret = this.query.value('flowise');
        if (ret == 'N/A') return null;

        return ret;
    }

    @computed get flowiseVars(): { [varName: string]: string } | null {
        if (!this.selectedReportId) return null;
        return this.flowiseVarsDB[this.selectedReportId] ?? null;
    }

    @computed
    get ragAPI(): RagAPI {
        if (!this.activeReportOriginal) {
            return null;
        }
        return new RagAPI(this.selectedReportId);
    }

    resetDataAccessCache() {
        this.reportsDataAccessCache = {};
    }

    createDataAccess(reportId: string) {
        if (this.reportsDataAccessCache[reportId]) {
            return this.reportsDataAccessCache[reportId];
        }

        //console.log('Creating data access for: ', reportId);
        const deal = this.reportDataDB[reportId]; //Fix over rides ?
        if (!deal || deal['status'] != 'completed') {
            console.log('No data found for: ', reportId);
            return null;
        }

        const override = this.reportDataOverridesDB[reportId];
        this.reportsDataAccessCache[reportId] = new DataAccessUtils(
            deal,
            override,
            reportId
        );
        return this.reportsDataAccessCache[reportId];
    }

    @computed
    get company_name() {
        return this.query?.value('company_name');
    }

    @computed
    get ticker() {
        return this.query?.value('ticker');
    }

    @computed get reportCreationDate() {
        if (!this.query?.value('created')) return null;

        return moment.unix(this.query?.value('created')).format('YYYY-MM-D');
    }

    @computed get errorMessageTicker() {
        if (!this.query?.value('error')) return null;
        return this.query?.value('error');
    }
    @computed get reportFiscalYear() {
        if (!this.query?.value('report_fiscal_year')) return null;

        return this.query?.value('report_fiscal_year');
    }

    @computed get companyOverview() {
        return this.query?.value('company_overview');
    }
}

export enum UIDealDetailsTabs {
    DASHBOARD = 'DASHBOARD',
    FLOWISE_CHAT = 'COMPETITORS',
    DETAILS = 'DETAILS',
    COMPARE = 'COMPARE',
    SCORE_BREAKDOWN = 'SUMMARY',
    CHAT = 'CHAT',
    FLOWISE = 'FLOWISE',
    WORKSTREAMS = 'WORKSTREAMS',
    OUTPUTS = 'OUTPUTS',
    TASK_VIEW_DEBUG = 'TASK_VIEW_DEBUG'
}

export type UIScrolling = 'UP' | 'DOWN' | 'NONE';
type CategoryType = 'category' | 'sub_category';
export type UICategoryFilter = { type: CategoryType; value: string };

export class StoreUI {
    get isDev() {
        return process.env.NODE_ENV === 'development';
    }

    resetViewerRequests() {
        this.pdfRequest = { page: -1, highlightTexts: [] };
    }
    resetDealView() {
        this.dealViewTab = UIDealDetailsTabs.DASHBOARD;
        if (this.isDev) {
            this.dealViewTab = UIDealDetailsTabs.DASHBOARD;
        }

        this.resetViewerRequests();
    }

    resetHome() {
        this.dealPageNameSearch = '';
        this.dealPageIndustrySearch = [];
        this.dealPageScoreSearch = [];
        this.dealPageSortOrder = 'newest';
    }

    onKeyUpReset() {
        this.isKeyControlKeyDown = false;
        this.scrolling = 'NONE';
        this.isKeyTabDown = false;
        this.isKeyShiftKeyDown = false;
        this.isKeyEscapeDown = false;
    }

    requestPdfImagePage(page: number) {
        this.resetViewerRequests();
        setTimeout(() => {
            this.pdfRequest = {
                page,
                highlightTexts: []
            };
        }, 10);
    }

    constructor() {
        this.resetDealView();
    }

    @observable accessor detailsViewYearsToShow = 3;

    @observable accessor pdfRequest: {
        page: number;
        highlightTexts?: string[];
    };

    @observable accessor disableLoading = false;
    @observable accessor forceLoading = false;
    @observable accessor scrolling: UIScrolling = 'NONE';

    @observable accessor currentSplitRatioDetailView = 50;
    @observable
    accessor modal: ModalDisplay = null;

    @observable accessor dealPageNameSearch: string = '';
    @observable accessor dealPageIndustrySearch: UICategoryFilter[] = [];
    @observable accessor dealPageScoreSearch: string[] = [];
    @observable accessor dealPageSortOrder: string = 'newest';

    @observable accessor pageTitle = '';

    @observable accessor dealViewTab: UIDealDetailsTabs;

    @observable accessor chatWaitingForResponse: boolean = false;

    @observable accessor isKeyControlKeyDown: boolean = false;

    @observable accessor isKeyTabDown: boolean = false;
    @observable accessor isKeyShiftKeyDown: boolean = false;
    @observable accessor isKeyEscapeDown: boolean = false;

    @observable accessor showEditedUnderlines: boolean = false;
}

export const useSetPageTitle = (pageTitle: string) => {
    useEffect(() => {
        storeUI.pageTitle = pageTitle;
    }, [pageTitle]);
};

interface IWorkstreamsGroup {
    title: string;
    task_ids: string[];
}

interface IClientSettings {
    onOff?: {
        showRagChat?: boolean;
        showAggregateScores?: boolean;
        showSubIndustryInHeader?: boolean;
        showBetaFeatures: boolean;
        onlyAdminsAddReports: boolean;
        enableSourcing?: boolean;
        showWorkstreams?: boolean;
        showPotentialCustomers?: boolean;
        showOutputsTab?: boolean;
    };

    isMaintenanceOn?: boolean;

    filterCompaniesForDemo: { [companId: string]: boolean };
    isDemoFilterOn?: boolean;
    industry_types_override?: IndustryType[];
    workstreams_groups?: IWorkstreamsGroup[];
}

interface IndustrySubtype {
    id: number;
    name: string;
}

interface IndustryType {
    id: number;
    name: string;
    subtypes: IndustrySubtype[];
}

import industry_json from '@assets/model/industry_data.json';
class ClientSettingsStore {
    set(data: any) {
        this.settings = data;
    }

    @observable accessor settings: IClientSettings = null;

    @computed get showRagChat() {
        return this.settings?.onOff?.showRagChat ?? false;
    }

    @computed get filterCompaniesForDemo() {
        return this.settings?.filterCompaniesForDemo ?? {};
    }

    @computed get isMaintenanceOn() {
        return this.settings?.isMaintenanceOn ?? false;
    }

    @computed get isDemoFilterOn() {
        return this.settings?.isDemoFilterOn ?? false;
    }

    @computed get onlyAdminsAddReports() {
        return this.settings?.onOff?.onlyAdminsAddReports ?? false;
    }

    @computed get showBetaFeatures() {
        return this.settings?.onOff?.showBetaFeatures ?? false;
    }

    @computed get showAggregateScores() {
        return this.settings?.onOff?.showAggregateScores ?? true;
    }

    @computed get showSubIndustryInHeader() {
        return this.settings?.onOff?.showSubIndustryInHeader ?? true;
    }

    @computed get enableSourcing() {
        return this.settings?.onOff?.enableSourcing ?? true;
    }

    @computed get showWorkstreams() {
        return this.settings?.onOff?.showWorkstreams ?? false;
    }

    @computed get showPotentialCustomers() {
        return this.settings?.onOff?.showPotentialCustomers ?? true;
    }

    @computed get showOutputsTab() {
        return this.settings?.onOff?.showOutputsTab ?? true;
    }

    @computed get industryTypes(): IndustryType[] {
        if (!this.settings?.industry_types_override) {
            return industry_json;
        }

        return this.settings?.industry_types_override;
    }

    @computed get workstreamsGroups(): IWorkstreamsGroup[] {
        return this.settings?.workstreams_groups ?? [];
    }
}

/*
    "dev_task_id": {
        "save_to": "variable name",
        "prequisite_variables": ["Var1", "Var2"],
        "starting_text": "Starting text",
        "support_file_upload": false,
        "on_complete": [],
        "inner_steps_names": ["Step1", "Step2"]
    }
    */

type PrerequisiteVariable = {
    name: string;
    display: string;
};

export type TaskEvalRule = {
    name: string;
    eval_prompt: string;
};

export type TaskEvalResults = {
    id: string;
    is_valid: boolean;
    reason: string;
};

export interface TaskDefinition {
    name: string;
    flow_id: string;
    save_to: string;
    prerequisite_variable: PrerequisiteVariable[];
    starting_text: string;
    support_file_upload: boolean;
    on_complete: string[];
    inner_steps_names: string[];
    evals?: { [key: string]: TaskEvalRule };
}

class TasksStore {
    @observable accessor tasksDefinitions: { [key: string]: TaskDefinition } =
        {};

    @observable accessor global_eval_rules: {
        [key: string]: TaskEvalRule;
    } = {};

    get_task_definition(task_id: string): TaskDefinition {
        return this.tasksDefinitions[task_id];
    }
}

const store = new Store();
const storeUI = new StoreUI();
const tasksStore = new TasksStore();

const clientSettingStore = new ClientSettingsStore();
export const useStore = () => store;
export const useStoreUI = () => storeUI;
export const useClientSettingStore = () => clientSettingStore;
export const useTasksStore = () => tasksStore;
